import { FAQ_ITEM_DEEP_LINK_PARAM } from "@frontend-shared/constants";
import { EGlobalSearchTargetKind } from "@frontend-shared/types";

import { FRESH_CHAT_LOCATION_STATE } from "@/constants/fresh-chat";
import {
  GLOBAL_SEARCH_DEEP_LINK_PARAMS as PARAMS,
  GLOBAL_SEARCH_DOCS_SEARCH_PATH,
} from "@/constants/global-search";
import { PATHS } from "@/constants/paths";
import { buildDocsHandoffPath } from "@/lib/docs-session";

import type {
  IResolveGlobalSearchDestinationProps,
  TGlobalSearchDestination,
} from "./types";
import { EGlobalSearchDestinationType } from "./types";

// NOTE: Mirrors ESettingsQueryParams (pages/settings/types.ts) — the settings
// page reads kebab-case keys, while shared targets carry camelCase ones.
const SETTINGS_QUERY = {
  SECTION: "section",
  TAB: "tab",
  ORGANIZATION_UUID: "organization-uuid",
  PROJECT_UUID: "project-uuid",
} as const;
const ORGANIZATION_SECTION = "organization";
const PROFILE_SECTION = "profile";
const HISTORY_TAB = "history";
// NOTE: NSAI-6657 — the project's Models page is this Profile settings tab.
const MODELS_TAB = "models";
const CUSTOM_INTEGRATIONS_TAB = "custom";

interface IBuildPathProps {
  pathname: string;
  // NOTE: Undefined and empty values are dropped, so optional params can be
  // passed through as they come.
  query?: Readonly<Record<string, string | undefined>>;
}

const buildPath = ({ pathname, query = {} }: IBuildPathProps): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") {
      search.set(key, value);
    }
  }
  const queryString = search.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

const withSegment = (pattern: string, segment: string): string =>
  pattern.replace(/:[A-Za-z]+$/, encodeURIComponent(segment));

const navigate = (path: string): TGlobalSearchDestination => ({
  type: EGlobalSearchDestinationType.NAVIGATE,
  path,
});

const openWindow = (path: string): TGlobalSearchDestination => ({
  type: EGlobalSearchDestinationType.OPEN_WINDOW,
  path,
});

interface IResolveSettingsPathProps {
  params: Readonly<Record<string, string>>;
  activeOrganizationUuid: string | null;
}

// NOTE: /settings?section=…&tab=…[&organization-uuid=…][&setting=…]. An
// organization target without its own organization opens the active
// project's; with neither, the organization list.
const resolveSettingsPath = ({
  params,
  activeOrganizationUuid,
}: IResolveSettingsPathProps): string => {
  const section = params.section ?? PROFILE_SECTION;
  const isOrganization = section === ORGANIZATION_SECTION;
  const organizationUuid = isOrganization
    ? (params.organizationUuid ?? activeOrganizationUuid ?? undefined)
    : undefined;

  return buildPath({
    pathname: PATHS.SETTINGS,
    query: {
      [SETTINGS_QUERY.SECTION]: section,
      [SETTINGS_QUERY.ORGANIZATION_UUID]: organizationUuid,
      [SETTINGS_QUERY.PROJECT_UUID]: isOrganization
        ? params.projectUuid
        : undefined,
      // NOTE: A tab of an organization means nothing on the organization
      // list, so it is only kept once an organization is known.
      [SETTINGS_QUERY.TAB]:
        isOrganization && !organizationUuid ? undefined : params.tab,
      [PARAMS.SETTING]: params.setting,
      [PARAMS.HIGHLIGHT]: params.highlight,
    },
  });
};

// NOTE: NSAI-6589 — the one place a platform-agnostic target becomes a web
// destination. Every EGlobalSearchTargetKind is answered; a target whose
// required id is missing falls back to the page that lists it.
export const resolveGlobalSearchDestination = ({
  target,
  activeOrganizationUuid,
  canEditCustomIntegrations,
}: IResolveGlobalSearchDestinationProps): TGlobalSearchDestination => {
  const { params } = target;

  switch (target.kind) {
    case EGlobalSearchTargetKind.NEW_CHAT:
      // NOTE: NSAI-6994 — "New chat" from the palette is the sidebar's button.
      return {
        type: EGlobalSearchDestinationType.NAVIGATE,
        path: PATHS.CHAT,
        state: FRESH_CHAT_LOCATION_STATE,
      };
    case EGlobalSearchTargetKind.CHAT:
      return navigate(
        params.chatUuid === undefined
          ? PATHS.CHAT
          : buildPath({
              pathname: `${PATHS.CHAT}/${encodeURIComponent(params.chatUuid)}`,
              query: { [PARAMS.CHAT_MESSAGE]: params.messageUuid },
            }),
      );
    case EGlobalSearchTargetKind.NEW_CHAT_IN_FOLDER:
      return navigate(
        buildPath({
          pathname: PATHS.CHAT,
          query: { [PARAMS.CHAT_FOLDER_ID]: params.folderUuid },
        }),
      );
    case EGlobalSearchTargetKind.CHAT_FOLDERS:
      return navigate(
        buildPath({
          pathname: PATHS.CHAT_FOLDERS,
          query: { [PARAMS.CHAT_FOLDER]: params.folderUuid },
        }),
      );
    case EGlobalSearchTargetKind.ASSISTANTS:
      return navigate(PATHS.ASSISTANTS);
    case EGlobalSearchTargetKind.ASSISTANT:
      // NOTE: Opening an assistant from search starts a chat with it — the
      // same thing the assistant card's primary action does.
      return navigate(
        buildPath({
          pathname: PATHS.CHAT,
          query: { [PARAMS.CHAT_ASSISTANT]: params.assistantUuid },
        }),
      );
    case EGlobalSearchTargetKind.PROMPT_LIBRARY:
      return navigate(
        buildPath({
          pathname: PATHS.PROMPT_LIBRARY,
          query: {
            [PARAMS.PROMPT_FOLDER]: params.folderUuid,
            [PARAMS.PROMPT]:
              params.folderUuid === undefined ? undefined : params.promptUuid,
          },
        }),
      );
    case EGlobalSearchTargetKind.INTEGRATIONS:
      return navigate(
        buildPath({
          pathname: PATHS.INTEGRATIONS,
          query: { [PARAMS.TAB]: params.tab },
        }),
      );
    case EGlobalSearchTargetKind.INTEGRATION_DETAILS:
      return navigate(
        params.provider === undefined
          ? PATHS.INTEGRATIONS
          : withSegment(PATHS.INTEGRATION_DETAILS, params.provider),
      );
    case EGlobalSearchTargetKind.CUSTOM_INTEGRATION:
      return navigate(
        canEditCustomIntegrations && params.customIntegrationUuid !== undefined
          ? withSegment(
              PATHS.CUSTOM_INTEGRATION_EDIT,
              params.customIntegrationUuid,
            )
          : buildPath({
              pathname: PATHS.INTEGRATIONS,
              query: { [PARAMS.TAB]: CUSTOM_INTEGRATIONS_TAB },
            }),
      );
    case EGlobalSearchTargetKind.SKILLS:
      return navigate(PATHS.SKILLS);
    case EGlobalSearchTargetKind.SKILL:
      return navigate(
        params.skillUuid === undefined
          ? PATHS.SKILLS
          : withSegment(PATHS.SKILL_DETAIL, params.skillUuid),
      );
    case EGlobalSearchTargetKind.SKILL_CATALOG:
      return navigate(PATHS.SKILL_CATALOG);
    case EGlobalSearchTargetKind.SKILL_CATALOG_ENTRY:
      return navigate(
        params.entryUuid === undefined
          ? PATHS.SKILL_CATALOG
          : withSegment(PATHS.SKILL_CATALOG_ENTRY, params.entryUuid),
      );
    case EGlobalSearchTargetKind.AGENTS:
      return navigate(
        buildPath({
          pathname: PATHS.AGENTS,
          query: {
            [PARAMS.AGENTS_TAB]: params.tab,
            [PARAMS.AGENTS_RUNS]: params.runs,
          },
        }),
      );
    case EGlobalSearchTargetKind.MODELS:
      return navigate(
        buildPath({
          pathname: PATHS.SETTINGS,
          query: {
            [SETTINGS_QUERY.SECTION]: PROFILE_SECTION,
            [SETTINGS_QUERY.TAB]: MODELS_TAB,
            [PARAMS.MODEL]: params.modelUuid,
          },
        }),
      );
    case EGlobalSearchTargetKind.KNOWLEDGE:
      return navigate(
        buildPath({
          pathname: PATHS.KNOWLEDGE,
          query: {
            [PARAMS.TAB]: params.tab,
            [PARAMS.KNOWLEDGE_FILE]: params.fileUuid,
          },
        }),
      );
    case EGlobalSearchTargetKind.CALL_ASSISTANT:
      return navigate(PATHS.CALL_ASSISTANT);
    case EGlobalSearchTargetKind.MEETING:
      return navigate(
        params.meetingUuid === undefined
          ? PATHS.CALL_ASSISTANT
          : withSegment(PATHS.MEETING_DETAILS, params.meetingUuid),
      );
    case EGlobalSearchTargetKind.UPLOADS:
      return navigate(PATHS.UPLOADS);
    case EGlobalSearchTargetKind.GENERATED_FILES:
      return navigate(PATHS.AI_GENERATED_FILES);
    case EGlobalSearchTargetKind.HTML_PREVIEW:
      return params.fileUuid === undefined
        ? navigate(PATHS.AI_GENERATED_FILES)
        : openWindow(withSegment(PATHS.HTML_PREVIEW, params.fileUuid));
    case EGlobalSearchTargetKind.DOC_RECOGNITION:
      return navigate(PATHS.DOC_RECOGNITION);
    case EGlobalSearchTargetKind.DOC_RECOGNITION_DOCUMENT:
      return navigate(
        params.documentUuid === undefined
          ? PATHS.DOC_RECOGNITION
          : withSegment(PATHS.DOC_RECOGNITION_DOCUMENT, params.documentUuid),
      );
    case EGlobalSearchTargetKind.ANALYTICS:
      return navigate(
        buildPath({
          pathname: PATHS.ANALYTICS,
          query: {
            [PARAMS.TAB]: params.tab,
            [PARAMS.PROBE]: params.probeUuid,
          },
        }),
      );
    case EGlobalSearchTargetKind.ANONYMIZATION_LAB:
      return navigate(PATHS.ANONYMIZATION_LAB);
    case EGlobalSearchTargetKind.SETTINGS:
      return navigate(resolveSettingsPath({ params, activeOrganizationUuid }));
    case EGlobalSearchTargetKind.FAQ:
      return navigate(
        buildPath({
          pathname: PATHS.FAQ,
          query: { [FAQ_ITEM_DEEP_LINK_PARAM]: params.itemId },
        }),
      );
    case EGlobalSearchTargetKind.SWITCH_PROJECT:
      return params.projectUuid === undefined
        ? navigate(PATHS.CHAT)
        : {
            type: EGlobalSearchDestinationType.SWITCH_PROJECT,
            projectUuid: params.projectUuid,
          };
    case EGlobalSearchTargetKind.OPEN_NOTIFICATION:
      return { type: EGlobalSearchDestinationType.OPEN_NOTIFICATIONS };
    case EGlobalSearchTargetKind.SEARCH_HISTORY:
      return navigate(
        buildPath({
          pathname: PATHS.SETTINGS,
          query: {
            [SETTINGS_QUERY.SECTION]: PROFILE_SECTION,
            [SETTINGS_QUERY.TAB]: HISTORY_TAB,
            [PARAMS.HISTORY_SEARCH]: params.query,
          },
        }),
      );
    case EGlobalSearchTargetKind.SEARCH_DOCS:
      // NOTE: NSAI-6750 — through the docs_session handoff, so the new tab
      // lands on the search results even when the docs cookie has expired.
      return openWindow(
        buildDocsHandoffPath(
          buildPath({
            pathname: GLOBAL_SEARCH_DOCS_SEARCH_PATH,
            query: { q: params.query },
          }),
        ),
      );
    default:
      // NOTE: A target kind this build does not know yet (a newer server).
      return navigate(PATHS.CHAT);
  }
};
